import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  IoAdd,
  IoBarcodeOutline,
  IoSearchOutline,
  IoCubeOutline,
  IoWarningOutline,
  IoClose,
  IoCheckmarkDone,
  IoCheckboxOutline,
  IoSwapHorizontalOutline,
  IoTimeOutline,
  IoTrashOutline,
  IoAlertCircleOutline,
  IoBanOutline,
  IoReceiptOutline,
} from "react-icons/io5";
import { toast } from "sonner";
import api, { fileUrl } from "@/api";
import { useI18n, STORE_LABELS, ALL_STORES } from "@/i18n";
import { AppHeader } from "@/components/AppHeader";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ExpiryDateInput } from "@/components/ExpiryDateInput";
import { ProductGridSkeleton } from "@/components/Skeletons";
import { useAuth } from "@/auth";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { tapLight } from "@/utils/haptics";
import { ReservationDrawer } from "@/components/ReservationDrawer";
import { MultiAddWizard } from "@/components/MultiAddWizard";
import { LotsDetailSheet } from "@/components/LotsDetailSheet";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription,
} from "@/components/ui/drawer";

// Stock thresholds (new): 0-20 = green (basso), 20-30 = orange (medio), 30+ = red (alto)
const isLowStock = (p) => Number(p.quantity || 0) <= 20;
const isMediumStock = (p) => {
  const q = Number(p.quantity || 0);
  return q > 20 && q <= 30;
};
const isHighStock = (p) => Number(p.quantity || 0) > 30;
const badgeClass = (p) => {
  const qty = Number(p.quantity || 0);
  if (qty > 30) return "tf-badge-red";
  if (qty > 20) return "tf-badge-yellow";
  return "tf-badge-green";
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((d - now) / (1000 * 60 * 60 * 24));
};

const productImageSrc = (p) => {
  if (p.image_file_id) return fileUrl(p.image_file_id);
  return p.image_url || "";
};

export default function CatalogPage() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { canAccessStore, isTitolare, isDirettore } = useAuth();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 250);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [quickAddMulti, setQuickAddMulti] = useState(null); // {barcode, name, image_url, image_file_id, existingId, source}
  const [qtyEdit, setQtyEdit] = useState(null); // {product, value}
  const [savingQty, setSavingQty] = useState(false);
  const [filter, setFilter] = useState("all"); // all | expiring | low
  const [financials, setFinancials] = useState(null);
  const [multiAddOpen, setMultiAddOpen] = useState(false);
  const [reserveTarget, setReserveTarget] = useState(null);
  const [lotsSheet, setLotsSheet] = useState(null); // {barcode, name, storeId}

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [transferOpen, setTransferOpen] = useState(false);
  const [expireOpen, setExpireOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [dest, setDest] = useState("");
  const [expDate, setExpDate] = useState("");
  const [expDestination, setExpDestination] = useState("");

  const selectedStore = storeId || ALL_STORES[0];
  const canEdit = canAccessStore(selectedStore);
  const catalogCacheKey = `tf_catalog_cache_${selectedStore}`;

  const inFlightRef = useRef(null);

  // Seed products from cache on first paint (stale-while-revalidate).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(catalogCacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.items) && parsed.items.length && !q) {
          setProducts(parsed.items);
          setLoading(false);
        }
      }
    } catch { /* corrupted cache */ }
  }, [selectedStore]);

  const load = useCallback(async () => {
    // Abort any in-flight request to avoid out-of-order setProducts.
    if (inFlightRef.current) {
      try { inFlightRef.current.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    inFlightRef.current = controller;
    setLoading(true);
    try {
      const { data } = await api.get(`/products`, { params: { store_id: selectedStore, q: debouncedQ }, signal: controller.signal });
      if (controller.signal.aborted) return;
      setProducts(data);
      // Persist only unfiltered lists to keep cache useful
      if (!debouncedQ) {
        try { localStorage.setItem(catalogCacheKey, JSON.stringify({ items: data, ts: Date.now() })); } catch { /* quota */ }
      }
      if (isTitolare) {
        try {
          const { data: fin } = await api.get(`/stores/${selectedStore}/financials`, { signal: controller.signal });
          if (!controller.signal.aborted) setFinancials(fin);
        } catch {
          if (!controller.signal.aborted) setFinancials(null);
        }
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError" || err?.code === "ERR_CANCELED") return;
      console.error("Catalog load failed:", err);
      toast.error("Errore caricamento prodotti");
    } finally {
      if (inFlightRef.current === controller) inFlightRef.current = null;
      setLoading(false);
    }
  }, [selectedStore, debouncedQ, isTitolare, catalogCacheKey]);

  useEffect(() => { load(); }, [load]);

  // Reload on tab focus so the catalog stays up-to-date after coming back from another app.
  useEffect(() => {
    const onFocus = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const handleDetected = async (code, mode = "single") => {
    // MULTI mode: don't close the scanner. Try to find the product locally to speed things up;
    // otherwise fetch OpenFoodFacts / cross-store data for pre-fill and open the mini-sheet.
    if (mode === "multi") {
      let base = { barcode: code, name: "", image_url: "", image_file_id: "", price: "", cost: "", notes: "", source: "" };
      try {
        const { data } = await api.get(`/products/scan/${encodeURIComponent(code)}`, { params: { store_id: selectedStore } });
        // Already exists in this store → we'll increment its quantity
        setQuickAddMulti({ ...base, existingId: data.id, name: data.name, image_url: data.image_url, image_file_id: data.image_file_id });
        return;
      } catch { /* not in current store */ }
      try {
        const { data } = await api.get(`/products/lookup/${encodeURIComponent(code)}`);
        setQuickAddMulti({ ...base, name: data?.name || "", image_url: data?.image_url || "", image_file_id: data?.image_file_id || "", price: data?.price ?? "", cost: data?.cost ?? "", notes: data?.notes || "", source: data?.source || "" });
      } catch {
        setQuickAddMulti(base);
      }
      return;
    }

    // SINGLE mode (previous behavior)
    setScannerOpen(false);
    try {
      const { data } = await api.get(`/products/scan/${encodeURIComponent(code)}`, {
        params: { store_id: selectedStore },
      });
      navigate(`/product/${data.id}`);
      return;
    } catch (err) {
      // not in current store — try lookup for prefill
    }
    if (!canEdit) {
      toast.info("Prodotto non presente in questo negozio");
      return;
    }
    try {
      const { data } = await api.get(`/products/lookup/${encodeURIComponent(code)}`);
      const params = new URLSearchParams({ store_id: selectedStore, barcode: code });
      if (data?.name) params.set("name", data.name);
      if (data?.article_code) params.set("article_code", data.article_code);
      if (data?.image_url) params.set("image_url", data.image_url);
      if (data?.image_file_id) params.set("image_file_id", data.image_file_id);
      if (data?.price != null && data.price !== "") params.set("price", String(data.price));
      if (data?.cost != null && data.cost !== "") params.set("cost", String(data.cost));
      if (data?.notes) params.set("notes", data.notes);
      if (data?.source) params.set("source", data.source);
      navigate(`/product/new?${params.toString()}`);
      if (data?.source === "internal") toast.success(`Trovato dai negozi: ${data.name}`);
      else if (data?.source === "openfoodfacts") toast.success(`Trovato: ${data.name}`);
      else toast.info("Prodotto nuovo — inserisci i dati");
    } catch {
      navigate(`/product/new?store_id=${selectedStore}&barcode=${encodeURIComponent(code)}`);
      toast.info("Prodotto nuovo — inserisci i dati");
    }
  };

  const commitQuickAddMulti = async (payload) => {
    // payload = { quantity }
    if (!quickAddMulti) return;
    const qty = Math.max(1, Number(payload.quantity || 1));
    try {
      if (quickAddMulti.existingId) {
        // Increment existing product quantity by qty
        const current = products.find((p) => p.id === quickAddMulti.existingId);
        const newQty = (current?.quantity || 0) + qty;
        await api.put(`/products/${quickAddMulti.existingId}`, { quantity: newQty });
        toast.success(`+${qty} → ${current?.name || "articolo"}`);
      } else {
        await api.post("/products", {
          store_id: selectedStore,
          name: quickAddMulti.name || `EAN ${quickAddMulti.barcode}`,
          barcode: quickAddMulti.barcode,
          quantity: qty,
          image_url: quickAddMulti.image_url || "",
          image_file_id: quickAddMulti.image_file_id || "",
          price: quickAddMulti.price !== "" ? Number(quickAddMulti.price) : null,
          cost: quickAddMulti.cost !== "" ? Number(quickAddMulti.cost) : null,
          notes: quickAddMulti.notes || "",
        });
        toast.success(`Aggiunto: ${quickAddMulti.name || quickAddMulti.barcode}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiunta");
    } finally {
      setQuickAddMulti(null);
    }
  };

  const openQtyEdit = (product, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!canEdit || selectMode) return;
    setQtyEdit({ product, value: product.quantity });
  };

  const saveQtyEdit = async () => {
    if (!qtyEdit) return;
    const newQty = Math.max(0, Number(qtyEdit.value || 0));
    if (newQty === qtyEdit.product.quantity) { setQtyEdit(null); return; }
    setSavingQty(true);
    try {
      await api.put(`/products/${qtyEdit.product.id}`, { quantity: newQty });
      toast.success(`Quantità: ${newQty} pz`);
      setQtyEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSavingQty(false);
    }
  };

  const displayed = useMemo(() => {
    let list = products;
    if (filter === "low") list = products.filter(isLowStock);
    else if (filter === "medium") list = products.filter(isMediumStock);
    else if (filter === "high") list = products.filter(isHighStock);
    else if (filter === "expiring") {
      list = products.filter((p) => {
        const d = daysUntil(p.expiry_date);
        return d !== null && d < 15;
      });
    }
    // Sort by quantity DESC (highest at top)
    return [...list].sort((a, b) => Number(b.quantity || 0) - Number(a.quantity || 0));
  }, [products, filter]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const startSelectMode = () => { setSelectMode(true); setSelected(new Set()); };
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };
  const selectAllToggle = () => {
    if (selected.size === displayed.length) setSelected(new Set());
    else setSelected(new Set(displayed.map((p) => p.id)));
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    try {
      await api.post("/products/bulk_delete", { ids: [...selected] });
      toast.success("Eliminati");
      exitSelectMode();
      load();
    } catch { toast.error("Errore"); }
  };

  const doBulkTransfer = async () => {
    if (!dest || selected.size === 0) return;
    try {
      const { data } = await api.post("/products/bulk_transfer", { ids: [...selected], destination_store_id: dest });
      toast.success(`Spostati ${data.moved} · Uniti ${data.merged}`);
      setTransferOpen(false); setDest(""); exitSelectMode(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const doBulkMoveExpired = async () => {
    if (selected.size === 0) return;
    if (!expDestination.trim()) {
      toast.error(t("destination_required"));
      return;
    }
    try {
      await api.post("/products/bulk_move_expired", {
        ids: [...selected],
        expiry_date: expDate || new Date().toISOString().slice(0, 10),
        destination: expDestination.trim(),
        notes: "",
      });
      toast.success("Spostati a Scaduti");
      setExpireOpen(false); setExpDate(""); setExpDestination(""); exitSelectMode(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const withBottomBar = selectMode && selected.size > 0;
  // The floating "Aggiunta prodotti" FAB sits at ~146px from viewport bottom (90px
  // offset + 56px height). Without extra bottom padding the last row of the grid
  // ends up hidden beneath it. Adds the fab-padding class whenever the FAB is
  // visible (edit mode + not in select mode) so the container gets enough room.
  const withFabPadding = !selectMode && canEdit;

  return (
    <div
      className={`mobile-frame ${withBottomBar ? "with-bottom-bar" : ""} ${withFabPadding ? "with-fab-padding" : ""}`}
      data-testid="catalog-page"
    >
      {selectMode ? (
        <div className="tf-header-simple">
          <button data-testid="select-cancel" className="tf-close-btn" onClick={exitSelectMode} aria-label={t("cancel")}>
            <IoClose size={22} />
          </button>
          <div className="tf-header-title-center" data-testid="page-title">
            {selected.size === 0
              ? t("select_all")
              : selected.size === 1
              ? t("selected_count_1")
              : t("selected_count_n").replace("$", selected.size)}
          </div>
          <button data-testid="select-all-btn" className="tf-icon-btn ghost" onClick={selectAllToggle} aria-label={t("select_all")}>
            <IoCheckmarkDone size={20} color="#E2001A" />
          </button>
        </div>
      ) : (
        <AppHeader
          title={t(STORE_LABELS[selectedStore])}
          showBack
          backTo="/"
          right={
            <>
              {isDirettore && !canEdit && (
                <button
                  data-testid="header-reserve-shortcut"
                  onClick={() => navigate("/reservations")}
                  className="tf-icon-btn ghost"
                  aria-label={t("reservations")}
                  title={t("reservations")}
                >
                  <IoSwapHorizontalOutline size={20} />
                </button>
              )}
              {canEdit && (
                <button
                  data-testid="header-select-btn"
                  onClick={startSelectMode}
                  className="tf-icon-btn ghost"
                  aria-label={t("select_all")}
                >
                  <IoCheckboxOutline size={20} />
                </button>
              )}
            </>
          }
        />
      )}

      <div className="tf-content">
        {isTitolare && financials && (
          <div className="tf-financial-card" data-testid="financials-card">
            <div className="tf-financial-row">
              <div className="tf-financial-cell">
                <span className="tf-financial-label">Costo totale</span>
                <span className="tf-financial-value" data-testid="financials-cost">€ {Number(financials.total_cost || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="tf-financial-cell">
                <span className="tf-financial-label">Valore totale</span>
                <span className="tf-financial-value tf-financial-value-strong" data-testid="financials-value">€ {Number(financials.total_value || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
            <div className="tf-small" style={{ marginTop: 8, color: "#000", fontWeight: 600 }}>
              {financials.n_products || 0} articoli · {financials.total_units || 0} pezzi
            </div>
          </div>
        )}

        {canEdit && (
          <div className="tf-field" style={{ marginBottom: 12 }}>
            <button
              type="button"
              data-testid="invoice-check-shortcut"
              className="tf-near-expiry-card"
              onClick={() => navigate(`/catalog/${selectedStore}/invoice-check`)}
              style={{
                background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
                color: "#ffffff",
                borderColor: "#2a2a2a",
              }}
            >
              <span className="tf-near-expiry-icon" style={{ background: "#ffffff", color: "#0a0a0a" }}>
                <IoReceiptOutline size={22} />
              </span>
              <span className="tf-near-expiry-body">
                <span className="tf-near-expiry-title" style={{ color: "#ffffff" }}>Controlla la tua fattura</span>
                <span className="tf-near-expiry-sub" style={{ color: "rgba(255,255,255,0.75)" }}>
                  Foto della bolla · spunta arrivati / mancanti
                </span>
              </span>
              <span className="tf-near-expiry-chev" style={{ color: "#ffffff" }}>›</span>
            </button>
          </div>
        )}

        {canEdit && (
          <div className="tf-field" style={{ marginBottom: 12 }}>
            <button
              type="button"
              data-testid="near-expiry-shortcut"
              className="tf-near-expiry-card"
              onClick={() => navigate(`/catalog/${selectedStore}/near-expiry`)}
            >
              <span className="tf-near-expiry-icon"><IoAlertCircleOutline size={22} /></span>
              <span className="tf-near-expiry-body">
                <span className="tf-near-expiry-title">Prossime Scadenze</span>
                <span className="tf-near-expiry-sub">Prodotti in scadenza a breve · aggiunta rapida</span>
              </span>
              <span className="tf-near-expiry-chev">›</span>
            </button>
          </div>
        )}

        <div className="tf-field" style={{ marginBottom: 12 }}>
          <div className="tf-input-wrap">
            <IoSearchOutline size={20} className="tf-input-icon-left" />
            <input
              data-testid="search-input"
              className="tf-input tf-input-with-icon-left tf-input-with-icon-right"
              placeholder={t("search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ paddingRight: 60 }}
            />
            <button
              type="button"
              data-testid="scan-open-btn"
              onClick={() => setScannerOpen(true)}
              className="tf-icon-btn red"
              style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", width: 40, height: 40 }}
              aria-label={t("scan_barcode")}
            >
              <IoBarcodeOutline size={20} />
            </button>
          </div>
        </div>

        <div className="tf-chips-row">
          <button data-testid="chip-all" className={`tf-chip ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>
            {t("filter_all")}
          </button>
          <button data-testid="chip-expiring" className={`tf-chip ${filter === "expiring" ? "active-red" : ""}`} onClick={() => setFilter("expiring")}>
            <span className="tf-alert-mini">!</span>
            {t("filter_expiring")}
          </button>
          <button data-testid="chip-low" className={`tf-chip ${filter === "low" ? "active" : ""}`} onClick={() => setFilter("low")}>
            <span className="tf-dot green" />
            {t("filter_low_stock")}
          </button>
          <button data-testid="chip-medium" className={`tf-chip ${filter === "medium" ? "active" : ""}`} onClick={() => setFilter("medium")}>
            <span className="tf-dot yellow" />
            {t("filter_medium_stock")}
          </button>
          <button data-testid="chip-high" className={`tf-chip ${filter === "high" ? "active" : ""}`} onClick={() => setFilter("high")}>
            <span className="tf-dot red" />
            {t("filter_high_stock")}
          </button>
        </div>

        {loading ? (
          <ProductGridSkeleton count={6} />
        ) : displayed.length === 0 ? (
          <div className="tf-empty">
            <IoCubeOutline size={44} />
            <div>{t("no_products")}</div>
          </div>
        ) : (
          <div className="tf-product-grid">
            {displayed.map((p) => {
              const bClass = badgeClass(p);
              const dLeft = daysUntil(p.expiry_date);
              const isExpired = dLeft !== null && dLeft < 0;
              const isCritical = dLeft !== null && dLeft >= 0 && dLeft <= 4;
              const src = productImageSrc(p);
              const isSelected = selected.has(p.id);
              return (
                <ProductCard
                  key={p.id}
                  product={p}
                  isSelected={isSelected}
                  selectMode={selectMode}
                  canEdit={canEdit}
                  canReserve={isDirettore && !canEdit}
                  bClass={bClass}
                  isCritical={isCritical}
                  isExpired={isExpired}
                  dLeft={dLeft}
                  src={src}
                  t={t}
                  onClick={() => selectMode ? toggleSelect(p.id) : navigate(`/product/${p.id}`)}
                  onLongPress={(e) => openQtyEdit(p, e)}
                  onReserve={(e) => { e?.preventDefault?.(); e?.stopPropagation?.(); tapLight(); setReserveTarget(p); }}
                  onLots={(prod) => { tapLight(); setLotsSheet({ storeId: selectedStore, barcode: prod.barcode, name: prod.name }); }}
                />
              );
            })}
          </div>
        )}
      </div>

      {!selectMode && canEdit && (
        <button
          data-testid="fab-add-product"
          className="tf-fab"
          onClick={() => setMultiAddOpen(true)}
        >
          <IoAdd size={22} />
          Aggiunta prodotti
        </button>
      )}

      <MultiAddWizard
        open={multiAddOpen}
        storeId={selectedStore}
        onClose={() => { setMultiAddOpen(false); load(); }}
      />

      <ReservationDrawer
        open={!!reserveTarget}
        onOpenChange={(v) => { if (!v) setReserveTarget(null); }}
        product={reserveTarget}
        onSuccess={() => {
          setReserveTarget(null);
          window.dispatchEvent(new CustomEvent("reservations-changed"));
        }}
      />

      {withBottomBar && (
        <div className="tf-select-bottom-bar" data-testid="select-bottom-bar">
          <button data-testid="bulk-transfer-btn" onClick={() => setTransferOpen(true)}>
            <IoSwapHorizontalOutline size={22} />
            {t("move")}
          </button>
          <button data-testid="bulk-move-expired-btn" onClick={() => setExpireOpen(true)}>
            <IoWarningOutline size={22} />
            {t("move_to_expired")}
          </button>
          <button data-testid="bulk-delete-btn" onClick={() => setConfirmDeleteOpen(true)}>
            <IoTrashOutline size={22} />
            {t("delete")}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={doBulkDelete}
        title={t("confirm_delete")}
        description={selected.size === 1 ? t("selected_count_1") : t("selected_count_n").replace("$", selected.size)}
        confirmLabel={t("delete")}
      />

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={handleDetected}
        allowMultiMode={canEdit}
      />

      <Drawer open={transferOpen} onOpenChange={setTransferOpen}>
        <DrawerContent data-testid="bulk-transfer-drawer">
          <DrawerHeader style={{ textAlign: "left" }}>
            <DrawerTitle>{t("transfer_title")}</DrawerTitle>
            <DrawerDescription>{selected.size} {t("products").toLowerCase()}</DrawerDescription>
          </DrawerHeader>
          <div style={{ padding: "0 20px 24px" }} data-vaul-no-drag onPointerDownCapture={(e) => e.stopPropagation()}>
            <div className="tf-field">
              <label className="tf-label">{t("destination")}</label>
              <select className="tf-select" value={dest} onChange={(e) => setDest(e.target.value)} data-testid="bulk-transfer-dest" data-vaul-no-drag>
                <option value="">—</option>
                {ALL_STORES.filter((s) => s !== selectedStore).map((s) => (
                  <option key={s} value={s}>{t(STORE_LABELS[s])}</option>
                ))}
              </select>
            </div>
            <button className="tf-btn-primary" onClick={doBulkTransfer} disabled={!dest} data-testid="bulk-transfer-confirm" data-vaul-no-drag>
              {t("confirm")}
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={expireOpen} onOpenChange={setExpireOpen}>
        <DrawerContent data-testid="bulk-expire-drawer">
          <DrawerHeader style={{ textAlign: "left" }}>
            <DrawerTitle>{t("move_to_expired")}</DrawerTitle>
            <DrawerDescription>{selected.size} {t("products").toLowerCase()}</DrawerDescription>
          </DrawerHeader>
          <div style={{ padding: "0 20px 24px" }} data-vaul-no-drag onPointerDownCapture={(e) => e.stopPropagation()}>
            <div className="tf-field">
              <label className="tf-label">{t("expiry_date")}</label>
              <ExpiryDateInput value={expDate} onChange={setExpDate} testIdPrefix="bulk-expire-date" />
            </div>
            <div className="tf-field">
              <label className="tf-label">{t("destination")} *</label>
              <input
                data-testid="bulk-expire-destination"
                className="tf-input"
                value={expDestination}
                onChange={(e) => setExpDestination(e.target.value)}
                placeholder={t("destination_placeholder")}
              />
            </div>
            <button className="tf-btn-primary" onClick={doBulkMoveExpired} data-testid="bulk-expire-confirm" data-vaul-no-drag>
              <IoTimeOutline size={18} />
              {t("confirm")}
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      {quickAddMulti && (
        <QuickAddSheet
          data={quickAddMulti}
          onCancel={() => setQuickAddMulti(null)}
          onSave={commitQuickAddMulti}
        />
      )}
      {qtyEdit && (
        <QuickQtyStepper
          product={qtyEdit.product}
          value={qtyEdit.value}
          onChange={(v) => setQtyEdit((s) => ({ ...s, value: v }))}
          onCancel={() => setQtyEdit(null)}
          onSave={saveQtyEdit}
          saving={savingQty}
        />
      )}
      <LotsDetailSheet
        open={!!lotsSheet}
        storeId={lotsSheet?.storeId}
        barcode={lotsSheet?.barcode}
        productName={lotsSheet?.name}
        onClose={() => setLotsSheet(null)}
      />
    </div>
  );
}

/** Product card with long-press for the quick quantity stepper. */
const ProductCard = memo(function ProductCard({ product: p, isSelected, selectMode, canEdit, canReserve, bClass, isCritical, isExpired, dLeft, src, t, onClick, onLongPress, onReserve, onLots }) {
  const timerRef = useRef(null);
  const suppressClickRef = useRef(false);
  const startPosRef = useRef(null);

  const onDown = (e) => {
    if (selectMode || !canEdit) return;
    const p2 = e.touches ? e.touches[0] : e;
    startPosRef.current = { x: p2.clientX, y: p2.clientY };
    timerRef.current = setTimeout(() => {
      suppressClickRef.current = true;
      onLongPress?.(e);
    }, 500);
  };
  const onMove = (e) => {
    if (!timerRef.current || !startPosRef.current) return;
    const p2 = e.touches ? e.touches[0] : e;
    const dx = Math.abs(p2.clientX - startPosRef.current.x);
    const dy = Math.abs(p2.clientY - startPosRef.current.y);
    if (dx > 10 || dy > 10) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const onUp = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setTimeout(() => { suppressClickRef.current = false; }, 60);
  };
  const onCancel = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  return (
    <button
      data-testid={`product-item-${p.id}`}
      className={`tf-product-card ${isSelected ? "selected" : ""}`}
      onClick={(e) => { if (suppressClickRef.current) { e.preventDefault(); e.stopPropagation(); return; } onClick(); }}
      onContextMenu={(e) => { e.preventDefault(); if (!selectMode && canEdit) onLongPress?.(e); }}
      onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp} onTouchCancel={onCancel}
      onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onCancel}
    >
      {selectMode && (
        <div className="tf-product-select-check">
          <IoCheckmarkDone size={16} />
        </div>
      )}
      <div className="tf-product-square-content">
        {isExpired && !selectMode && (
          <span
            data-testid={`product-expired-${p.id}`}
            title={`Scaduto da ${Math.abs(dLeft)} giorni`}
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: "#0a0a0a",
              color: "#ffffff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
              zIndex: 2,
            }}
          >
            <IoBanOutline size={14} strokeWidth={2.5} />
          </span>
        )}
        {isCritical && !isExpired && !selectMode && (
          <span className="tf-product-alert-inline" data-testid={`product-critical-${p.id}`} title={`Scade fra ${dLeft} giorni`}>!</span>
        )}
        <div className="tf-product-name">{p.name}</div>
        <span data-testid={`product-quantity-${p.id}`} className={`tf-product-qty-badge-inline ${bClass}`}>
          {p.quantity} {t("pcs")}
        </span>
        {p.lots_count > 1 && !selectMode && (
          <span
            role="button"
            tabIndex={0}
            data-testid={`product-lots-${p.id}`}
            aria-label={`Vedi ${p.lots_count} lotti`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLots?.(p); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onLots?.(p); } }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              marginTop: 4,
              padding: "2px 8px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              background: "rgba(226, 0, 26, 0.08)",
              color: "var(--tf-brand, #E2001A)",
              border: "1px solid rgba(226, 0, 26, 0.25)",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <IoTimeOutline size={12} />
            {p.lots_count} lotti
          </span>
        )}
      </div>
      {canReserve && !selectMode && (
        <div
          role="button"
          tabIndex={0}
          className="tf-product-reserve-bar"
          data-testid={`product-reserve-${p.id}`}
          aria-label="Richiedi prodotto"
          onClick={onReserve}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onReserve?.(e); }}
        >
          <IoSwapHorizontalOutline size={16} />
          <span>Richiedi</span>
        </div>
      )}
    </button>
  );
});

/** Quick +/- stepper modal for on-the-spot quantity edits (long-press card). */
function QuickQtyStepper({ product, value, onChange, onCancel, onSave, saving }) {
  return (
    <div className="tf-qty-quick-overlay" data-testid="qty-quick-overlay" onClick={onCancel}>
      <div className="tf-qty-quick-card" onClick={(e) => e.stopPropagation()}>
        <div className="tf-qty-quick-title">{product.name}</div>
        <div className="tf-qty-quick-sub">Quantità</div>
        <div className="tf-qty-quick-stepper" data-testid="qty-quick-stepper">
          <button
            type="button"
            data-testid="qty-quick-minus"
            className="tf-qty-quick-btn"
            disabled={value <= 0}
            onClick={() => onChange(Math.max(0, Number(value) - 1))}
            aria-label="Diminuisci"
          >−</button>
          <input
            type="number"
            className="tf-qty-quick-value"
            data-testid="qty-quick-value"
            value={value}
            min={0}
            onChange={(e) => onChange(Math.max(0, Number(e.target.value || 0)))}
            onFocus={(e) => { if (String(value) === "0") { onChange(""); } e.target.select(); }}
            style={{ background: "transparent", border: "none", outline: "none" }}
          />
          <button
            type="button"
            data-testid="qty-quick-plus"
            className="tf-qty-quick-btn"
            onClick={() => onChange(Number(value) + 1)}
            aria-label="Aumenta"
          >+</button>
        </div>
        <div className="tf-qty-quick-actions">
          <button
            type="button"
            data-testid="qty-quick-cancel"
            className="tf-qty-quick-cancel"
            onClick={onCancel}
            disabled={saving}
          >Annulla</button>
          <button
            type="button"
            data-testid="qty-quick-save"
            className="tf-qty-quick-save"
            onClick={onSave}
            disabled={saving}
          >Salva</button>
        </div>
      </div>
    </div>
  );
}

/** Multi-scan quick-add sheet: 1 tap +/– then Save; scanner reopens for next. */
function QuickAddSheet({ data, onCancel, onSave }) {
  const [qty, setQty] = useState(1);
  const label = data.existingId
    ? `Aggiungi a "${data.name}"`
    : (data.name ? `Nuovo: ${data.name}` : `Nuovo articolo (EAN ${data.barcode})`);
  return (
    <div className="tf-qty-quick-overlay" data-testid="quickadd-overlay" onClick={onCancel}>
      <div className="tf-qty-quick-card" onClick={(e) => e.stopPropagation()}>
        <div className="tf-qty-quick-title">{label}</div>
        <div className="tf-qty-quick-sub">
          {data.existingId ? "Somma alla giacenza" : `Codice ${data.barcode}`}
        </div>
        <div className="tf-qty-quick-stepper">
          <button
            type="button"
            data-testid="quickadd-minus"
            className="tf-qty-quick-btn"
            disabled={qty <= 1}
            onClick={() => setQty((v) => Math.max(1, v - 1))}
            aria-label="Diminuisci"
          >−</button>
          <input
            type="number"
            className="tf-qty-quick-value"
            data-testid="quickadd-qty"
            value={qty}
            min={1}
            onChange={(e) => setQty(Math.max(1, Number(e.target.value || 1)))}
            style={{ background: "transparent", border: "none", outline: "none" }}
          />
          <button
            type="button"
            data-testid="quickadd-plus"
            className="tf-qty-quick-btn"
            onClick={() => setQty((v) => v + 1)}
            aria-label="Aumenta"
          >+</button>
        </div>
        <div className="tf-qty-quick-actions">
          <button
            type="button"
            data-testid="quickadd-cancel"
            className="tf-qty-quick-cancel"
            onClick={onCancel}
          >Annulla</button>
          <button
            type="button"
            data-testid="quickadd-save"
            className="tf-qty-quick-save"
            onClick={() => onSave({ quantity: qty })}
          >Aggiungi</button>
        </div>
      </div>
    </div>
  );
}
